import { Globe, Loader2, RefreshCw } from 'lucide-react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { fetchAiStats, fetchInvestorFlow, fetchMacro, fetchMarketEvents, fetchMarketLandscape, fetchMarketOutlook, fetchMarketScenarios, fetchMarketStructure, fetchMarketTrackStats } from '../api';
import { AiStats, FearGreed, LiveInvestorFlow, MacroItem, UpcomingEvent, MarketLandscape, MarketOutlookSignals, MarketScenarios, MarketStructure, MarketTrackStats } from '../types';
import { isOwner } from '../owner';
import ForecastCard from './ForecastCard';
import EasyMarketDeck from './EasyMarketDeck';

// 🎨 규격 V2(08-23) 공용 부품 — 버튼은 고스트/켜짐 두 가지, 칩은 한 규격, 섹션 라벨은 키커.
//   시장 탭은 08-23 리디자인에서 보라 계열을 전부 걷어내고 잉크+앰버 한 포인트로 통일했다.
const BTN = 'inline-flex items-center gap-1.5 h-[26px] px-2 sm:h-[29px] sm:px-[11px] rounded-lg border text-[11.5px] sm:text-xs transition-colors';
const GHOST = `${BTN} border-[#202A3C] text-dc-ink2/90 font-medium hover:border-[#33405A] hover:text-dc-ink1 disabled:opacity-40 disabled:hover:border-[#202A3C]`;
const ON = `${BTN} border-dc-point/35 bg-dc-point/10 text-dc-point font-semibold`;
const CHIP = 'inline-flex items-center h-[22px] px-2 rounded-md border text-[11.5px] font-medium whitespace-nowrap';
const MINICHIP = 'inline-flex items-center h-[18px] px-1.5 rounded-[5px] border text-[10.5px] font-medium whitespace-nowrap';
const KICKER = 'text-[10.5px] font-[650] tracking-[.12em] text-dc-ink3';
const PLAIN = 'tracking-normal font-normal';   // 키커 안에 붙는 설명 문구(자간 원복)
const INSET = 'rounded-lg border border-dc-line1 bg-dc-bg1/60';
// 앰버 강조 박스 — 내일 브리핑·소문 일정처럼 "미리 알아야 손해 안 보는" 줄
const AMBER_BOX = 'rounded-lg border border-dc-point/25 bg-dc-point/[0.04] px-3.5 py-3';
// 🌡️ 온도·감정 그라데이션 표준형 — 왼쪽=차가움(공포) → 오른쪽=뜨거움(탐욕). 바늘은 앰버.
const TEMP_GRAD = 'linear-gradient(90deg, rgba(92,143,214,.55), #232B3A 45%, #232B3A 55%, rgba(224,108,91,.55))';

// 🎨 상태 3단(규격 V2 전 화면 공통) — 정상=ok / 주의=앰버 / 경고=warn.
//   down(파랑)은 방향(하락·매도) 전용이라 상태색으로 쓰지 않는다 — 쓰면 '경고'가 '주의'보다 순해 보여 위계가 꼬인다.
const STATUS_STYLE: Record<string, string> = {
  '정상': 'border-dc-ok/35 bg-dc-ok/[0.06] text-dc-ok',
  '주의': 'border-dc-point/40 bg-dc-point/[0.08] text-dc-point',
  '경고': 'border-dc-warn/40 bg-dc-warn/[0.08] text-dc-warn',
};

// 시나리오 색 — AI가 이름에 설명을 덧붙여 보내므로(예: '조정 (5~10% 추가 하락)')
// 정확일치 대신 키워드 포함으로 판별. 급락/강세를 조정(하락)보다 먼저 검사.
//   ⚠️ 08-23 한국식으로 정정 — 강세=웜(up)/약세=쿨(down). 옛 초록·노랑·빨강은 상세 패널과 뜻이 뒤집혀 있었다.
function scenStyle(name: string): { bar: string; text: string } {
  if (name.includes('강세') || name.includes('상승')) return { bar: 'bg-dc-up', text: 'text-dc-up' };
  if (name.includes('급락') || name.includes('폭락')) return { bar: 'bg-dc-down', text: 'text-dc-down' };
  if (name.includes('조정') || name.includes('하락')) return { bar: 'bg-dc-down/55', text: 'text-dc-down/85' };
  return { bar: 'bg-dc-ink3', text: 'text-dc-ink2' };
}

// 미니 스파크라인 — 점수 흐름을 작은 선그래프로(오르면 웜·내리면 쿨).
function Sparkline({ values }: { values: number[] }) {
  if (values.length < 2) return null;
  const w = 56, h = 16;
  const min = Math.min(...values), max = Math.max(...values), range = max - min || 1;
  const pts = values
    .map((v, i) => `${((i / (values.length - 1)) * w).toFixed(1)},${(h - ((v - min) / range) * h).toFixed(1)}`)
    .join(' ');
  const up = values[values.length - 1] >= values[0];
  return (
    <svg width={w} height={h} preserveAspectRatio="none" className="shrink-0">
      <polyline points={pts} fill="none" stroke={up ? '#E06C5B' : '#5C8FD6'} strokeWidth="1.5" strokeLinejoin="round" strokeLinecap="round" />
    </svg>
  );
}

// 공포·탐욕 점수대별 색 (0 차가운 공포 → 100 뜨거운 탐욕 — 온도계와 같은 방향)
function fgZone(score: number): { text: string; fill: string } {
  if (score < 25) return { text: 'text-dc-down', fill: 'bg-dc-down' };
  if (score < 45) return { text: 'text-dc-down/80', fill: 'bg-dc-down/70' };
  if (score <= 55) return { text: 'text-dc-ink2', fill: 'bg-dc-ink3' };
  if (score < 75) return { text: 'text-dc-up/85', fill: 'bg-dc-up/70' };
  return { text: 'text-dc-up', fill: 'bg-dc-up' };
}

// 📍 KOSPI 20일 미니 차트 — 지금 위치를 한눈에.
function MiniKospi({ series }: { series: number[] }) {
  if (!series || series.length < 2) return null;
  const w = 90, h = 26;
  const min = Math.min(...series), max = Math.max(...series), range = max - min || 1;
  const pts = series.map((v, i) => `${((i / (series.length - 1)) * w).toFixed(1)},${(h - ((v - min) / range) * h).toFixed(1)}`).join(' ');
  const up = series[series.length - 1] >= series[0];
  return (
    <svg width={w} height={h} preserveAspectRatio="none" className="shrink-0">
      <polyline points={pts} fill="none" stroke={up ? '#E06C5B' : '#5C8FD6'} strokeWidth="1.5" strokeLinejoin="round" />
    </svg>
  );
}

// 📍 지금 어디 — 고점 대비·하락일·국면일 (지형도의 첫 줄, 30초 방문 첫 답)
function PositionHeader({ p }: { p: NonNullable<MarketLandscape['position']> }) {
  const off = p.off_high_pct;
  const offCls = off == null ? 'text-dc-ink2' : off <= -15 ? 'text-dc-down' : off <= -7 ? 'text-dc-point' : 'text-dc-ink2';
  return (
    <div className="mb-3 pb-3 border-b border-dc-hair flex items-center gap-3">
      <MiniKospi series={p.series} />
      <div className="min-w-0 text-[11.5px] leading-snug">
        <p className="text-dc-ink1"><span className="text-dc-ink3">코스피</span> <b className="font-mono">{p.kospi.toLocaleString('ko-KR')}</b>
          {off != null && <span className={offCls}> · 고점 대비 <b>{off}%</b></span>}</p>
        <p className="text-dc-ink3">
          {p.down_days >= 1 && <span className="text-dc-down">하락 {p.down_days}일째 · </span>}
          {p.regime && <span>{p.regime} 국면 {p.regime_days}일째</span>}
          {p.high != null && <span className="text-dc-ink3/80"> · 고점 {p.high.toLocaleString('ko-KR')}</span>}
        </p>
      </div>
    </div>
  );
}

// 🔀 어제와 달라진 것 — 상시 켜진 위험배너 대신, 방문할 때마다 새 정보(변화만).
function DeltaLine({ d }: { d: NonNullable<MarketLandscape['delta']> }) {
  const c = d.changes;
  const parts: string[] = [];
  if (c.regime) parts.push(`국면 ${c.regime[0]}→${c.regime[1]}`);
  if (c.crash) parts.push(`급락 시나리오 ${c.crash[0]}→${c.crash[1]}%`);
  if (c.fear) parts.push(`공포탐욕 ${c.fear[0]}→${c.fear[1]}`);
  if (c.vix) parts.push(`VIX ${c.vix[0]}→${c.vix[1]}`);
  if (c.breadth) parts.push(`상승종목 ${c.breadth[0]}→${c.breadth[1]}%`);
  return (
    <div className="mb-3 pb-3 border-b border-dc-hair text-[11.5px] flex flex-wrap items-baseline gap-x-2">
      <span className={KICKER}>어제와 달라진 것</span>
      {d.same || parts.length === 0 ? <span className="text-dc-ink3">거의 같음 — 쉬어가는 흐름</span>
        : <span className="text-dc-ink2">{parts.join(' · ')}</span>}
    </div>
  );
}

// 공포·탐욕 힘 세기 — 한국 합성(주) + CNN 미국(부), 그라데이션 바에 두 바늘 + 요소 분해(접이식).
function FearGreedGauge({ fg, trend }: { fg: FearGreed; trend?: number[] }) {
  const { kr, cnn } = fg;
  if (!kr && !cnn) return null;
  const krZone = kr ? fgZone(kr.score) : null;
  return (
    <div className="mb-4 pb-4 border-b border-dc-hair">
      <div className="flex flex-wrap items-baseline gap-x-2 mb-3">
        <p className={KICKER}>공포·탐욕 지수</p>
        <span className="text-dc-ink3 text-[10.5px]">역발상 — 극공포=기회 / 극탐욕=과열경계</span>
      </div>

      {/* ④ 추이 — 극단이 며칠째, 어제보다 풀리는지/깊어지는지(역발상 타이밍의 핵심) */}
      {trend && trend.length >= 2 && (() => {
        const cur = trend[trend.length - 1], prev = trend[trend.length - 2];
        const diff = Math.round(cur - prev);
        const extreme = cur < 25 ? '극공포' : cur > 75 ? '극탐욕' : null;
        let days = 0;
        if (extreme) for (let i = trend.length - 1; i >= 0; i--) { const e = trend[i] < 25 ? '극공포' : trend[i] > 75 ? '극탐욕' : null; if (e === extreme) days++; else break; }
        return (
          <div className="flex items-center gap-2 mb-3 text-[10.5px]">
            <span className="text-dc-ink3">추이</span>
            <Sparkline values={trend} />
            {extreme && <span className="text-dc-ink2">{extreme} {days}일째</span>}
            <span className={diff > 0 ? 'text-dc-up' : diff < 0 ? 'text-dc-down' : 'text-dc-ink3'}>
              어제보다 {diff >= 0 ? '+' : ''}{diff}{diff > 0 ? ' 풀리는 중' : diff < 0 ? ' 깊어지는 중' : ''}
            </span>
          </div>
        );
      })()}

      {/* 그라데이션 바 + KR(위 앰버바늘)·CNN(아래 잉크바늘)
          ⚠️ 08-23 색만 표준형으로 교체 — 왼쪽=차가움(공포)/오른쪽=뜨거움(탐욕)은 옛 빨강→초록 때와 같은 뜻이라
             위치식(left: score%)은 손대지 않았다. 0점=극공포=왼쪽 그대로. */}
      <div className="relative h-[5px] rounded-full max-w-[420px] mb-2" style={{ background: TEMP_GRAD }}>
        {kr && (
          <div className="absolute top-[-4px] -translate-x-1/2" style={{ left: `${kr.score}%` }} title={`한국 ${kr.score}`}>
            <div className="w-[2px] h-[13px] rounded-[2px] bg-dc-point" />
          </div>
        )}
        {cnn && (
          <div className="absolute bottom-[-4px] -translate-x-1/2" style={{ left: `${cnn.score}%` }} title={`미국 ${cnn.score}`}>
            <div className="w-[2px] h-[13px] rounded-[2px] bg-dc-ink2/70" />
          </div>
        )}
      </div>
      <div className="flex justify-between max-w-[420px] text-[11.5px] text-dc-ink3 mb-3">
        <span>극공포</span><span>중립</span><span>극탐욕</span>
      </div>

      {/* KR 주 + CNN 부 */}
      <div className="flex items-baseline gap-2 flex-wrap">
        {kr && (
          <div className="flex items-baseline gap-1.5">
            <span className="text-[15.5px] leading-none">{kr.emoji}</span>
            <span className="text-dc-ink3 text-[10.5px]">🇰🇷 한국</span>
            <span className={`font-mono text-[17.5px] font-[750] ${krZone!.text}`}>{kr.score}</span>
            <span className={`text-[11.5px] font-semibold ${krZone!.text}`}>{kr.label}</span>
          </div>
        )}
        {cnn && (
          <div className="flex items-baseline gap-1 ml-auto">
            <span className="text-dc-ink3 text-[10.5px]">🇺🇸 미국·CNN</span>
            <span className={`font-mono text-[13px] font-bold ${fgZone(cnn.score).text}`}>{cnn.score}</span>
            <span className={`text-[11.5px] ${fgZone(cnn.score).text}`}>{cnn.label}</span>
          </div>
        )}
      </div>

      {/* 한·미 디커플링 한 줄 (차이 클 때만) */}
      {kr && cnn && Math.abs(kr.score - cnn.score) >= 20 && (
        <p className="text-dc-ink3 text-[10.5px] mt-2 leading-snug">
          한·미 심리 디커플링({Math.abs(kr.score - cnn.score)}p) — {kr.score < cnn.score ? '한국이 더 공포(외국인 매도 한국 집중 가능성)' : '한국이 더 탐욕(국내 쏠림 주의)'}.
        </p>
      )}

      {/* 요소 분해 — 왜 이 점수인지 (탭 분리돼 항상 펼침) */}
      {kr && kr.components.length > 0 && (
            <div className="mt-3 space-y-1">
              <p className={`${KICKER} mb-1`}>구성 요소</p>
              {kr.components.map((c) => (
                <div key={c.name} className="flex items-center gap-2">
                  <span className="text-dc-ink2 text-[10.5px] w-16 shrink-0">{c.name}</span>
                  <div className="flex-1 h-1 bg-dc-hair rounded-full overflow-hidden">
                    <div className={`h-full ${fgZone(c.greed).fill}`} style={{ width: `${c.greed}%` }} />
                  </div>
                  <span className="font-mono text-dc-ink3 text-[10.5px] w-7 text-right shrink-0">{c.greed}</span>
                  <span className="text-dc-ink3/80 text-[10.5px] w-28 shrink-0 text-right truncate" title={c.detail}>{c.detail}</span>
                </div>
              ))}
            </div>
      )}
    </div>
  );
}

// 장중 미국 선물 — 한국 장중 '오늘 밤 미국 방향' 가늠자. /api/macro에서 선물만 뽑아 표시(30초 갱신).
function FuturesBlock() {
  const [futures, setFutures] = useState<MacroItem[]>([]);
  useEffect(() => {
    const load = () =>
      fetchMacro()
        .then((d) => setFutures(d.items.filter((i) => i.label === '나스닥선물' || i.label === 'S&P선물')))
        .catch(() => {});
    load();
    const t = setInterval(load, 30000);
    return () => clearInterval(t);
  }, []);
  if (futures.length === 0) return null;
  return (
    <div className="mb-4 pb-4 border-b border-dc-hair">
      <p className={`${KICKER} mb-2`}>
        장중 미국 선물 <span className={`${PLAIN} text-dc-ink3/80`}>— 한국 장중 실시간 '오늘 밤 미국 방향' 가늠자 (현물은 어젯밤 종가)</span>
      </p>
      <div className="flex flex-wrap gap-x-5 gap-y-1">
        {futures.map((f) => {
          const v = f.change_pct ?? 0;
          const up = v > 0, down = v < 0;
          const c = up ? 'text-dc-up' : down ? 'text-dc-down' : 'text-dc-ink2'; // 한국식 상승=웜/하락=쿨
          return (
            <div key={f.label} className="flex items-baseline gap-1.5">
              <span className="text-dc-ink2 text-[11.5px]">{f.label}</span>
              <span className={`font-mono text-[13px] font-bold tabular-nums ${c}`}>
                {up ? '▲' : down ? '▼' : '─'}{Math.abs(v).toFixed(2)}%
              </span>
              {f.value !== null && (
                <span className="text-dc-ink3 font-mono text-[10.5px]">
                  {Number(f.value).toLocaleString('en-US', { maximumFractionDigits: 0 })}
                </span>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

// 🧠 AI 판단 검증(관리자 전용) — "AI를 믿어도 되나"를 데이터로. 적중률 리더보드가 아니라 보정·편향 점검 도구.
function hrCls(v: number | null): string {
  return v == null ? 'text-dc-ink3' : v >= 55 ? 'text-dc-ok' : v >= 45 ? 'text-dc-ink2' : 'text-dc-warn';
}
function AiVerifyPanel({ track, ai }: { track: MarketTrackStats | null; ai: AiStats | null }) {
  const hr = (v: number | null) => (v == null ? '─' : `${v}%`);
  // 시장: 표본 기간 코스피가 그냥 다 올랐으면(up_ratio↑) 강세예측 적중은 베타
  const td = track?.to_date;
  const betaWarn = td && td.up_ratio >= 70;
  // 종목: 공매도(비중축소) 0건이고 매수가 압도면 강세 일색 → 멜트업 베타 의심
  const permabull = ai && ai.bias.bear === 0 && ai.bias.bull >= ai.bias.neutral;
  return (
    <div className={`mt-3 ${INSET} px-3 py-2.5 text-[11.5px]`}>
      <p className="text-dc-ink1 text-[14px] font-[750] mb-2">AI 판단 검증 <span className="text-dc-ink3 text-[11.5px] font-normal">— AI를 믿어도 되나(적중률 자랑이 아니라 편향·보정 점검). 표본 적으면 참고만.</span></p>

      {/* ① 시장 국면 — 시나리오 예측 vs 실제 코스피 */}
      <div className="mb-3">
        <p className="text-dc-ink2 font-medium mb-1">① 시장 시나리오 예측 <span className="text-dc-ink3 font-normal">(강세/조정/급락 예측이 실제 코스피와 맞았나)</span></p>
        {!track || track.n_snapshots < 2 ? (
          <p className="text-dc-ink3">기록 축적 중 — 매일 1건씩 쌓임. 며칠 지나야 평가 가능.</p>
        ) : (
          <div className="space-y-0.5 pl-1">
            <div className="flex flex-wrap items-center gap-x-3">
              {td ? <span className="text-dc-ink2">현재까지 적중 <b className={hrCls(td.hit_rate)}>{hr(td.hit_rate)}</b> <span className="text-dc-ink3">({td.hits}/{td.n})</span></span>
                  : <span className="text-dc-ink3">평가 가능한 과거 예측 부족</span>}
              {track.horizons['5'] && <span className="text-dc-ink3">5일 <b className={hrCls(track.horizons['5'].hit_rate)}>{hr(track.horizons['5'].hit_rate)}</b> ({track.horizons['5'].n})</span>}
              {track.horizons['20'] && <span className="text-dc-ink3">20일 <b className={hrCls(track.horizons['20'].hit_rate)}>{hr(track.horizons['20'].hit_rate)}</b> ({track.horizons['20'].n})</span>}
            </div>
            {Object.keys(track.by_scenario).length > 0 && (
              <div className="flex flex-wrap items-center gap-x-3 text-dc-ink3">
                {(['강세', '조정', '급락'] as const).map((s) => track.by_scenario[s] && (
                  <span key={s}>{s} 예측 <b className={hrCls(track.by_scenario[s].hit_rate)}>{hr(track.by_scenario[s].hit_rate)}</b> ({track.by_scenario[s].n})</span>
                ))}
              </div>
            )}
            {td && <p className={betaWarn ? 'text-dc-warn' : 'text-dc-ink3'}>
              표본 기간 코스피 평균 <b>{td.avg_kospi_ret >= 0 ? '+' : ''}{td.avg_kospi_ret}%</b>·상승비율 <b>{td.up_ratio}%</b>
              {betaWarn ? ' — ⚠️ 거의 다 오른 장이라 「강세 적중」은 실력보다 베타(시장 덕)일 수 있음' : ''}
            </p>}
            {track.avg_probs && (() => {
              const ap = track.avg_probs; const down = (ap['조정'] ?? 0) + (ap['급락'] ?? 0); const up = ap['강세'] ?? 0;
              const lean = down > up + 20 ? '하방(조정·급락) 쪽으로 치우침' : up > down + 20 ? '강세 쪽으로 치우침' : '한쪽 쏠림 적음';
              return <p className="text-dc-ink3">평균 확률 배분 강세 {up}·조정 {ap['조정'] ?? '─'}·급락 {ap['급락'] ?? '─'} <span className="text-dc-ink2">→ 예측 편향: {lean}</span></p>;
            })()}
          </div>
        )}
      </div>

      {/* ② 종목 심층분석 — 행동결론 적중 */}
      <div className="pt-2.5 border-t border-dc-hair">
        <p className="text-dc-ink2 font-medium mb-1">② 종목 심층분석 결론 <span className="text-dc-ink3 font-normal">(매수/관망/비중축소가 실제로 맞았나 · 연속 분석 실현수익 기준)</span></p>
        {!ai || ai.resolved === 0 ? (
          <p className="text-dc-ink3">채점 가능한 기록 부족 — 같은 종목을 두 번 이상 분석해야 그 사이 수익으로 채점됨(미결 {ai?.open ?? 0}).</p>
        ) : (
          <div className="space-y-0.5 pl-1">
            <div className="flex flex-wrap items-center gap-x-3">
              <span className="text-dc-ink2">전체 적중 <b className={hrCls(ai.hit_rate)}>{hr(ai.hit_rate)}</b> <span className="text-dc-ink3">(채점 {ai.decided}/{ai.resolved}·미결 {ai.open})</span></span>
              {ai.avg_days != null && <span className="text-dc-ink3">평균 {ai.avg_days}일 보유 기준</span>}
            </div>
            <div className="flex flex-wrap items-center gap-x-3 text-dc-ink3">
              {(['매수·분할매수', '관망', '비중축소'] as const).map((k) => {
                const a = ai.by_stance[k]; if (!a) return null;
                return <span key={k}>{k} <b className={hrCls(a.hit_rate)}>{hr(a.hit_rate)}</b> <span className="text-dc-ink3">({a.decided}/{a.observed})</span></span>;
              })}
            </div>
            <p className={permabull ? 'text-dc-warn' : 'text-dc-ink3'}>
              결론 편향 — 매수 {ai.bias.bull}·관망 {ai.bias.neutral}·비중축소 {ai.bias.bear}건
              {permabull ? ' — ⚠️ 강세 일색(비중축소 0). 다 오른 장에선 매수 적중이 실력인지 베타인지 구분 어려움' : ''}
            </p>
          </div>
        )}
      </div>
      <p className="text-dc-ink3 text-[10.5px] mt-2 leading-snug">표본이 충분히 쌓이기 전엔 적중률 숫자보다 <b>편향·베타 경고</b>를 보세요. 이 지표는 점수에 안 들어가고(표시·신뢰용), 멜트업 끝나면 더 의미 있어집니다.</p>
    </div>
  );
}

export default function MarketOutlook() {
  const [sig, setSig] = useState<MarketOutlookSignals | null>(null);
  const [expert, setExpert] = useState(false);   // 🔬 전문가 뷰 접힘(초보자 데크가 1면)
  const [scen, setScen] = useState<MarketScenarios | null>(null);
  const [loadingScen, setLoadingScen] = useState(false);
  const [land, setLand] = useState<MarketLandscape | null>(null); // 📍 지형도(위치·델타·공포탐욕 추이)
  const [structure, setStructure] = useState<MarketStructure | null>(null); // 🏦 구조 계기판 + 내일 브리핑(줍줍 3단계)
  const [liveFlow, setLiveFlow] = useState<LiveInvestorFlow | null>(null); // 장중 잠정 수급(폴링)
  const [events, setEvents] = useState<UpcomingEvent[]>([]);                 // 📅 임박·회자 이벤트(뉴스 감지)
  // 🧠 AI 판단 검증(관리자 전용) — 펼칠 때 한 번만 로드
  const [trackStats, setTrackStats] = useState<MarketTrackStats | null>(null);
  const [aiStats, setAiStats] = useState<AiStats | null>(null);
  useEffect(() => {   // 📊 성적표 통합(전문가 뷰 재편 07-07) — 토글 없이 펼치면 자동 로드
    if (!expert || trackStats) return;
    fetchMarketTrackStats().then(setTrackStats).catch(() => {});
    fetchAiStats().then(setAiStats).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [expert]);

  // 요청 ID — StrictMode 이중 마운트/수동 재시도가 겹쳐도 '가장 마지막' 응답만 반영.
  // 늦게 온 응답이 먼저 뜬 AI 내용을 덮어쓰는 걸 막아 '갑자기 사라짐'을 방지.
  const reqRef = useRef(0);

  const loadScenarios = useCallback((force = false) => {
    const id = ++reqRef.current;
    setLoadingScen(true);
    fetchMarketScenarios(force)   // force=true(🔄 재시도)면 서버 캐시 무시하고 새로 받음
      .then((d) => { if (reqRef.current === id) setScen(d); })
      .catch(() => {})   // 실패해도 이미 떠 있는 내용을 지우지 않음(빈 화면 방지)
      .finally(() => { if (reqRef.current === id) setLoadingScen(false); });
  }, []);

  useEffect(() => {
    fetchMarketOutlook().then(setSig).catch(() => {});
    fetchMarketEvents().then((d) => setEvents(d.events || [])).catch(() => {});
    fetchMarketLandscape().then(setLand).catch(() => {});   // 📍 지형도
    fetchMarketStructure().then(setStructure).catch(() => {});   // 🏦 구조 계기판 + 내일 브리핑
    loadScenarios();
  }, [loadScenarios]);

  // 장중 잠정 수급 — 60초 폴링으로 수급 줄만 실시간 갱신(무거운 AI 분석과 분리).
  // 장 마감 후엔 그날 값이 고정이므로 폴링 중단(서버 market_active=false).
  useEffect(() => {
    let timer: ReturnType<typeof setInterval> | null = null;
    const tick = () => fetchInvestorFlow().then((d) => {
      setLiveFlow(d);
      if (!d.market_active && timer) { clearInterval(timer); timer = null; }
    }).catch(() => {});
    tick();
    timer = setInterval(tick, 60000);
    return () => { if (timer) clearInterval(timer); };
  }, []);

  if (!sig) return null;
  const b = sig.breadth || {};
  // AI가 일시 혼잡(과부하)이면 시나리오가 비어 fallback만 옴 — 계산값만 표시 중임을 알린다.
  const aiBusy = !loadingScen && !!scen && (scen.scenarios?.length ?? 0) === 0;

  return (
    <div className="max-w-[1060px]">
      {/* 🌦️ 초보자 데크(07-07 재설계) — 쉬움≠얕음: 모든 카드에 실측 숫자·왜? 펼침. 아래 전문가 뷰는 완전 보존. */}
      <EasyMarketDeck sig={sig} />

      {/* 🌙 내일 브리핑 — 다음 거래일 예정 이벤트(달력 확정). 밤새 보유 판단 손실 방지의 낮 버전. 상시 노출 */}
      {structure?.tomorrow && structure.tomorrow.events.length > 0 && (
        <div className={`mt-5 ${AMBER_BOX}`}>
          <p className="text-[10.5px] font-[650] tracking-[.12em] text-dc-point mb-1.5">내일 브리핑 <span className={`${PLAIN} text-dc-ink3`}>— {structure.tomorrow.date?.slice(5).replace('-', '/')} 예정(달력 확정 — 아는 폭풍은 맞지 않기)</span></p>
          <div className="space-y-0.5">
            {structure.tomorrow.events.map((e, i) => (
              <p key={i} className="text-[13px] text-dc-ink2 leading-snug"><b className="text-dc-ink1 font-semibold">{e.name}</b> <span>· {e.note}</span></p>
            ))}
          </div>
        </div>
      )}

      {/* 🏦 구조 계기판 — 하이닉스 신용융자·공매도 추이(느린 구조 신호). 전부 % 단위·잠정·참고. 상시 노출 */}
      {structure?.hyx_credit_short && (structure.hyx_credit_short.credit || structure.hyx_credit_short.short) && (() => {
        const cs = structure.hyx_credit_short!;
        const arrow = (t: string) => t === 'up' ? '▲' : t === 'down' ? '▼' : '→';
        return (
          <div className="mt-5 flex flex-col gap-2">
            <p className={KICKER}>구조 계기판 <span className={`${PLAIN} text-dc-ink3/80`}>— 하이닉스 신용·공매도(며칠 단위 느린 신호)</span></p>
            <div className="flex flex-wrap gap-x-5 gap-y-1 text-[11.5px]">
              {cs.credit && (
                <span className="text-dc-ink2" title="신용융자 잔고율 — 빚내서 산 물량 비중. 늘수록 하락 시 반대매매(강제 매도) 연료가 쌓임">
                  빚투 잔고율 <b className="font-mono text-dc-ink1">{cs.credit.rate}%</b>{' '}
                  <span className={cs.credit.trend === 'up' ? 'text-dc-warn' : cs.credit.trend === 'down' ? 'text-dc-down' : 'text-dc-ink3'}>
                    {arrow(cs.credit.trend)} {cs.credit.trend === 'up' ? '늘어나는 중(연료↑)' : cs.credit.trend === 'down' ? '줄어드는 중' : '그대로'}
                  </span>
                </span>
              )}
              {cs.short && (
                <span className="text-dc-ink2" title="누적 공매도 잔고 비중 — 하락 베팅이 쌓인 정도. 늘면 하방 압력, 줄면 숏커버(되사기) 반등 여지">
                  공매도 잔고 <b className="font-mono text-dc-ink1">{cs.short.acc_ratio}%</b>{' '}
                  <span className={cs.short.trend === 'up' ? 'text-dc-down' : cs.short.trend === 'down' ? 'text-dc-up' : 'text-dc-ink3'}>
                    {arrow(cs.short.trend)} {cs.short.trend === 'up' ? '쌓이는 중(하방)' : cs.short.trend === 'down' ? '풀리는 중(반등 여지)' : '그대로'}
                  </span>
                  {cs.short.day_ratio != null && <span className="text-dc-ink3"> · 최근일 {cs.short.day_ratio}%</span>}
                </span>
              )}
            </div>
          </div>
        );
      })()}

      <button onClick={() => setExpert((v) => !v)} className="w-full mt-5 mb-4 flex items-center gap-2 text-left text-[13px] text-dc-ink3 hover:text-dc-ink2 border-t border-dc-hair pt-3.5 transition-colors">
        자세히 보기 — 시나리오·진입 점수·수급·성적표 (전문가 뷰) <span className="ml-auto text-dc-point/85">{expert ? '▾ 접기' : '▸ 펼치기'}</span>
      </button>
      {expert && (<>
      <div className="flex flex-wrap items-center gap-x-2 gap-y-1.5 mb-4">
        <Globe size={14} className="text-dc-ink3 shrink-0" />
        <span className="text-dc-ink1 text-[14px] font-[750] shrink-0">시장 분석</span>
        {scen?.regime_label && (
          <span className={`${CHIP} border-dc-line2 bg-dc-bg2 text-dc-ink1`}>
            {scen.regime_label}
          </span>
        )}
        <span className="text-dc-ink3 text-[10.5px] ml-auto whitespace-nowrap">{scen?.ai_model || ''}</span>
        <button
          onClick={() => loadScenarios(true)}
          disabled={loadingScen}
          title="AI 분석 새로 받기 (캐시 무시 · 페이지 새로고침 없이)"
          className={`${aiBusy ? ON : GHOST} shrink-0 whitespace-nowrap`}
        >
          <RefreshCw size={11} className={loadingScen ? 'animate-spin' : ''} /> 재시도
        </button>

      </div>

      {/* 📍 지금 어디 — 고점 대비·하락일·국면일 + KOSPI 20일 미니차트(지형도의 첫 줄) */}
      {land?.position && <PositionHeader p={land.position} />}
      {/* 🔀 어제와 달라진 것 — 상시 켜진 위험배너 대신, 방문할 때마다 '무엇이 바뀌었나' */}
      {land?.delta && <DeltaLine d={land.delta} />}

      {/* 📅 임박·회자 이벤트 레이더 — 뉴스에서 잡은 연기금 리밸런싱·MSCI·만기 등(예측 아님·회자되는 일정) */}
      {events.length > 0 && (
        <div className={`mb-4 ${AMBER_BOX}`}>
          <p className="text-[10.5px] font-[650] tracking-[.12em] text-dc-point mb-1.5">소문·회자 일정 <span className={`${PLAIN} text-dc-ink3`}>— 뉴스에서 감지(연기금·MSCI 등). 달력 확정 일정은 위 데크의 '조심할 날'에</span></p>
          <div className="space-y-0.5">
            {events.slice(0, 4).map((e, i) => (
              <p key={i} className="text-[13px] text-dc-ink2 leading-snug">
                <span className="text-dc-ink1 font-semibold">{e.category}</span> <span>· {e.headline}</span>
              </p>
            ))}
          </div>
        </div>
      )}


      <p className={`${KICKER} mt-5 mb-2`}>수급·심리 <span className={`${PLAIN} text-dc-ink3/80`}>· 미국 선물 = 오늘 밤 가늠자 / 공포탐욕 = 반대로 보는 눈</span></p>
      {/* 장중 미국 선물 — '오늘 밤 미국 방향' 가늠자 */}
      {<FuturesBlock />}

      {/* 공포·탐욕 지수 — 한국 합성 힘 세기 + CNN 미국 (역발상 심리 렌즈) */}
      {sig.fear_greed && (sig.fear_greed.kr || sig.fear_greed.cnn) && (
        <FearGreedGauge fg={sig.fear_greed} trend={land?.fear_series} />
      )}

      <p className={`${KICKER} mt-5 mb-2`}>시나리오 <span className={`${PLAIN} text-dc-ink3/80`}>· 점예측 아님 — 확률·트리거·뒤집힘 조건</span></p>
      {/* 시나리오 확률 — 한눈에 보이는 스택 막대 (강세/조정/급락). 상세는 아래 토글에 */}
      {scen?.scenarios && scen.scenarios.length > 0 && (
        <div className="mb-4 pb-4 border-b border-dc-hair">
          <p className="text-dc-ink2 text-[11.5px] font-medium mb-2">향후 시나리오 확률</p>
          <div className="flex h-1 rounded-full overflow-hidden bg-dc-hair mb-2 max-w-[420px]">
            {scen.scenarios.map((s, i) => {
              const c = scenStyle(s.name);
              return (
                <div
                  key={i}
                  className={`${c.bar} h-full`}
                  style={{ width: `${s.probability}%` }}
                  title={`${s.name} ${s.probability}%`}
                />
              );
            })}
          </div>
          <div className="flex flex-wrap gap-x-4 gap-y-1">
            {scen.scenarios.map((s, i) => {
              const c = scenStyle(s.name);
              return (
                <div key={i} className="flex items-center gap-1.5">
                  <span className={`w-2 h-2 rounded-full ${c.bar}`} />
                  <span className="text-dc-ink2 text-[11.5px]">{s.name}</span>
                  <span className={`font-mono text-[11.5px] font-semibold tabular-nums ${c.text}`}>{s.probability}%</span>
                </div>
              );
            })}
          </div>
        </div>
      )}


      <p className={`${KICKER} mt-5 mb-2`}>진입·관리 <span className={`${PLAIN} text-dc-ink3/80`}>· 신호판(대기 모드) / 감시판(보유 모드)</span></p>
      {/* 진입 신호판 (현금 대기 모드 — 자동 감지) */}
      {sig.mode === 'entry_wait' && sig.entry && (
        <div className="mb-2.5">
          {/* 헤더 — 점수 크게 / 핵심 / 판정 분리 */}
          <div className="flex items-center gap-2 mb-1">
            <p className="text-dc-ink2 text-[11.5px] font-medium">진입 신호판 <span className="text-dc-ink3 font-normal">— 현금 대기 중</span></p>
            <span className="ml-auto text-[17.5px] font-[750] text-dc-ink1 tabular-nums">{sig.entry.score ?? 0}<span className="text-dc-ink3 text-[11.5px] font-normal">/100</span></span>
            <span className={`${MINICHIP} border-dc-line2 bg-dc-bg2 text-dc-ink2`}>핵심 {sig.entry.core_met ?? 0}/3</span>
          </div>
          <p className="text-dc-ink1 text-[13px] font-medium mb-2">→ {sig.entry.verdict}</p>

          {/* 진입 점수 힘 세기 — 0~100을 막대로(달성 앰버 / 미달 회색 — 규격 V2) */}
          {(() => {
            const sc = sig.entry.score ?? 0;
            const c = sc >= 70 ? 'bg-dc-point' : sc >= 40 ? 'bg-dc-point/60' : 'bg-dc-ink3';
            return (
              <div className="w-full max-w-[420px] h-1 bg-dc-hair rounded-full overflow-hidden mb-3">
                <div className={`h-full rounded-full ${c} transition-all`} style={{ width: `${sc}%` }} />
              </div>
            );
          })()}

          {/* 진입 점수 추이 (타이밍 온도계) — 최근 며칠 점수 흐름을 스파크라인으로 */}
          {sig.entry_history && sig.entry_history.length >= 2 && (() => {
            const h = sig.entry_history.slice(-6);
            const first = h[0].score, last = h[h.length - 1].score;
            const dir = last > first ? { t: `↑ 개선 (+${last - first})`, c: 'text-dc-up' }
              : last < first ? { t: `↓ 둔화 (${last - first})`, c: 'text-dc-down' }
              : { t: '→ 보합', c: 'text-dc-ink2' };
            return (
              <div className="text-[10.5px] mb-3 flex items-center gap-2">
                <span className="text-dc-ink3">추이</span>
                <Sparkline values={h.map((p) => p.score)} />
                <span className="font-mono text-dc-ink2">{first} → {last}</span>
                <span className={`font-medium ${dir.c}`}>{dir.t}</span>
              </div>
            );
          })()}

          {/* 진입 불/베어 토론 (지금 들어가자 vs 더 기다리자 — 서로 반박) */}
          {scen?.entry_bull && scen?.entry_bear && (
            <div className="mb-3 grid grid-cols-1 gap-2">
              <div className="rounded-lg border border-dc-up/25 bg-dc-up/[0.05] px-3 py-2.5">
                <p className="text-dc-up text-[10.5px] font-bold mb-1">지금 진입 (낙관)</p>
                <p className="text-dc-ink2 text-[11.5px] leading-snug">{scen.entry_bull}</p>
              </div>
              <div className="rounded-lg border border-dc-down/25 bg-dc-down/[0.05] px-3 py-2.5">
                <p className="text-dc-down text-[10.5px] font-bold mb-1">더 대기 (비관)</p>
                <p className="text-dc-ink2 text-[11.5px] leading-snug">{scen.entry_bear}</p>
              </div>
            </div>
          )}

          {/* AI 종합 판단 — 헤드라인(먼저) */}
          <div className={`mb-3 ${INSET} px-3 py-2.5`}>
            <p className="text-dc-ink2 text-[11.5px] font-medium mb-1">종합 판단 <span className="text-dc-ink3 font-normal">(★핵심 신호를 가장 무겁게)</span></p>
            {loadingScen && !scen ? (
              <p className="flex items-center gap-1.5 text-dc-ink2 text-[11.5px]"><Loader2 size={12} className="animate-spin" /> 판단 정리 중...</p>
            ) : scen?.entry_opinion ? (
              <p className="text-dc-ink1 text-[13px] leading-[1.55]">{scen.entry_opinion}</p>
            ) : (
              <>
                <p className="text-dc-ink2 text-[11.5px]">{sig.entry.score ?? 0}점 · 핵심 {sig.entry.core_met ?? 0}/3 — {sig.entry.verdict}.</p>
                {aiBusy && (
                  <p className="text-dc-point/85 text-[10.5px] mt-1.5 flex items-center gap-1">
                    AI가 일시 혼잡해 계산값만 표시 중 — 위 <RefreshCw size={10} className="inline" /> 재시도를 누르거나 잠시 후 다시 보세요.
                  </p>
                )}
              </>
            )}
          </div>

          {/* 근거 체크리스트 — ★핵심 강조 */}
          <p className={`${KICKER} mb-1.5`}>근거 신호 <span className={`${PLAIN} text-dc-ink3/80`}>(★=핵심, 가장 무겁게 반영)</span></p>
          <div className="space-y-1">
            {sig.entry.checks.map((c, i) => {
              const failedCore = c.core && !c.met;   // 실패한 핵심 = 약세색 강조
              return (
                <div key={i} className={`flex items-center gap-2 rounded-lg px-2.5 py-2 ${
                  failedCore ? 'border border-dc-down/30 bg-dc-down/[0.05]'
                    : c.core ? 'border border-dc-line1 bg-dc-bg1/60' : 'bg-dc-bg1/40'
                }`}>
                  <span className={`shrink-0 w-4 h-4 rounded-full flex items-center justify-center text-[10.5px] font-bold ${
                    c.met ? 'border border-dc-ok/40 bg-dc-ok/[0.12] text-dc-ok'
                      : failedCore ? 'border border-dc-down/40 bg-dc-down/[0.12] text-dc-down'
                      : 'border border-dc-line1 bg-dc-bg2 text-dc-ink3'
                  }`}>{c.met ? '✓' : failedCore ? '✗' : '·'}</span>
                  <span className={`text-[11.5px] font-medium shrink-0 truncate w-32 sm:w-40 ${
                    c.met ? 'text-dc-ink1' : failedCore ? 'text-dc-down' : 'text-dc-ink2'
                  }`}>
                    {c.core && <span className={failedCore ? 'text-dc-down' : 'text-dc-point'}>★ </span>}{c.name}
                  </span>
                  <span className="text-dc-ink1 font-mono text-[11.5px]">{c.current}</span>
                  <span className="text-dc-ink3 text-[11.5px] ml-auto text-right hidden sm:block">목표 {c.want}</span>
                </div>
              );
            })}
            {/* 뉴스·공시 신호 (AI 판단 — scen 로드 후) */}
            {scen?.news_signal && (
              <div className="flex items-center gap-2 rounded-lg px-2.5 py-2 bg-dc-bg1/40">
                <span className={`shrink-0 w-4 h-4 rounded-full flex items-center justify-center text-[10.5px] font-bold ${
                  scen.news_signal.met ? 'border border-dc-ok/40 bg-dc-ok/[0.12] text-dc-ok' : 'border border-dc-line1 bg-dc-bg2 text-dc-ink3'
                }`}>{scen.news_signal.met ? '✓' : '·'}</span>
                <span className={`text-[11.5px] font-medium shrink-0 truncate w-32 sm:w-40 ${scen.news_signal.met ? 'text-dc-ink1' : 'text-dc-ink2'}`}>
                  뉴스·공시 <span className="text-dc-ink3 font-normal">(AI)</span>
                </span>
                <span className="text-dc-ink1 font-mono text-[11.5px]">{scen.news_signal.label}</span>
                <span className="text-dc-ink3 text-[11.5px] ml-auto text-right hidden sm:block truncate max-w-[200px]" title={scen.news_signal.detail}>{scen.news_signal.detail}</span>
              </div>
            )}
          </div>
        </div>
      )}

      {/* 감시 신호판 (보유 모드 — 리스크 관리 / 관리 탭) */}
      {sig.mode !== 'entry_wait' && scen?.watchlist && scen.watchlist.length > 0 && (
        <div className="mb-2.5">
          <p className={`${KICKER} mb-1.5`}>
            감시 신호판 <span className={`${PLAIN} text-dc-ink3/80`}>— 이게 바뀌면 시나리오도 바뀜</span>
          </p>
          <div className="space-y-1">
            {scen.watchlist.map((w, i) => {
              const dot = w.status === '경고' ? 'bg-dc-warn' : w.status === '주의' ? 'bg-dc-point' : 'bg-dc-ok';
              return (
                <div key={i} className="flex items-center gap-2 bg-dc-bg1/40 rounded-lg px-2.5 py-2">
                  <span className={`w-[7px] h-[7px] rounded-full shrink-0 ${dot}`} />
                  <span className="text-dc-ink1 text-[11.5px] font-medium w-24 sm:w-28 shrink-0 truncate">{w.name}</span>
                  <span className="text-dc-ink1 font-mono text-[13px] font-semibold">{w.current}</span>
                  <span className="text-dc-ink3 text-[11.5px] ml-auto text-right hidden sm:block">기준 {w.threshold}</span>
                  <span className={`${MINICHIP} shrink-0 ${STATUS_STYLE[w.status] ?? 'border-dc-line1 bg-dc-bg1 text-dc-ink2'}`}>
                    {w.status}
                  </span>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* 상승종목 비율(breadth) — 작은 막대로 시각화 (요약 탭).
          null이면 장초반 일봉 미갱신 등 데이터 미준비 → 가짜 0% 대신 '집계전' 표시. */}
      {(
      <div className="flex items-center gap-2 text-[11.5px] mt-4 mb-1">
        <span className="text-dc-ink3 w-14 shrink-0">상승종목</span>
        {b.pct_advancing == null ? (
          <span className="text-dc-ink3" title="개별주 일간 등락률이 아직 집계되지 않음(장 시작 직후 등). 약세 신호 아님.">
            집계전 — 장초반 데이터 대기
          </span>
        ) : (
          <>
            <div className="flex-1 max-w-[140px] h-1 bg-dc-hair rounded-full overflow-hidden">
              <div className="h-full rounded-full bg-dc-point" style={{ width: `${b.pct_advancing}%` }} />
            </div>
            <span className="font-mono text-dc-ink2 tabular-nums">{b.pct_advancing}%</span>
          </>
        )}
      </div>
      )}

      {/* 오늘 투자자별 수급 — 장중엔 liveFlow(60초 폴링 잠정치) 우선, 없으면 outlook 스냅샷 (요약 탭) */}
      {(() => {
        const investors = liveFlow?.markets ?? sig.investors;
        return investors && Object.keys(investors).length > 0 && (
        <div className="mt-3 pt-3 border-t border-dc-hair">
          <p className="text-dc-ink3 text-[10.5px] mb-2 flex flex-wrap items-center gap-x-1.5">
            <span>오늘 수급 <span className="text-dc-ink3/80">(억원 · </span>
            <span className="text-dc-up">매수+</span><span className="text-dc-ink3/80"> / </span><span className="text-dc-down">매도−</span>
            <span className="text-dc-ink3/80">)</span></span>
            {liveFlow?.provisional ? (
              <span className="inline-flex items-center gap-1 text-dc-point">
                <span className="w-1.5 h-1.5 rounded-full bg-dc-point animate-pulse" />
                장중 잠정 {liveFlow.as_of} 기준
              </span>
            ) : (
              <span className="text-dc-ink3/80">{liveFlow?.as_of ? `${liveFlow.as_of} 기준 · ` : ''}네이버 참고</span>
            )}
          </p>
          {Object.entries(investors).map(([mkt, f]) => {
            const rows = [['외국인', f.foreign], ['기관', f.institution], ['연기금', f.pension], ['개인', f.individual]] as const;
            // ③ 수급 '흐름' — 오늘 숫자 위에 연속일·20일 누적(추세 전환은 흐름이 먼저 말해줌)
            const eok = (v: number) => (Math.abs(v) >= 10000 ? `${(Math.abs(v) / 10000).toFixed(1)}조` : `${Math.round(Math.abs(v)).toLocaleString('ko-KR')}억`);
            const flow: string[] = [];
            if (f.foreign_streak) flow.push(`외국인 ${Math.abs(f.foreign_streak)}일 ${f.foreign_streak > 0 ? '연속 순매수' : '연속 순매도'}`);
            if (f.foreign_20d != null) flow.push(`20일 ${f.foreign_20d > 0 ? '+' : '−'}${eok(f.foreign_20d)}`);
            if (f.inst_streak) flow.push(`기관 ${Math.abs(f.inst_streak)}일 ${f.inst_streak > 0 ? '순매수' : '순매도'}`);
            return (
              <div key={mkt} className="mb-2 last:mb-0">
                <div className="flex flex-wrap items-center gap-x-4 gap-y-1">
                  <span className="text-dc-ink2 text-[11.5px] font-medium w-12 shrink-0">{mkt}</span>
                  {rows.map(([label, val]) => {
                    const v = val ?? 0;
                    return (
                      <span key={label} className="text-[11.5px] whitespace-nowrap">
                        <span className={label === '연기금' ? 'text-dc-point/85' : 'text-dc-ink3'}>{label} </span>
                        <b className={`font-mono tabular-nums font-semibold ${v > 0 ? 'text-dc-up' : v < 0 ? 'text-dc-down' : 'text-dc-ink2'}`}>
                          {val == null ? '─' : `${v > 0 ? '+' : ''}${v.toLocaleString('ko-KR')}`}
                        </b>
                      </span>
                    );
                  })}
                </div>
                {flow.length > 0 && <p className="text-dc-ink3 text-[10.5px] pl-12 mt-0.5">흐름: {flow.join(' · ')}</p>}
              </div>
            );
          })}
        </div>
        );
      })()}

      {/* 시나리오별 트리거·뒤집힐 조건 (시나리오 탭 — 탭 분리돼 항상 펼침) */}
      {scen?.scenarios && scen.scenarios.length > 0 && (
            <div className="space-y-2 mt-4">
              <p className={KICKER}>시나리오별 트리거 · 뒤집힐 조건</p>
              {scen.scenarios.map((s, i) => {
                const c = scenStyle(s.name);
                return (
                  <div key={i} className={`${INSET} px-3 py-2.5`}>
                    <div className="flex items-center gap-2 mb-1.5">
                      <span className={`text-[11.5px] font-bold ${c.text}`}>{s.name}</span>
                      <span className="text-dc-ink1 text-[11.5px] font-mono ml-auto">{s.probability}%</span>
                    </div>
                    <div className="w-full bg-dc-hair rounded-full h-1 mb-2">
                      <div className={`${c.bar} h-1 rounded-full`} style={{ width: `${s.probability}%` }} />
                    </div>
                    {s.trigger && <p className="text-dc-ink2 text-[11.5px] leading-snug"><span className="text-dc-ink3">트리거:</span> {s.trigger}</p>}
                    {s.flip && <p className="text-dc-ink2 text-[11.5px] leading-snug"><span className="text-dc-ink3">뒤집힐 조건:</span> {s.flip}</p>}
                  </div>
                );
              })}
              {scen.bear_type && (
                <p className="text-dc-ink2 text-[11.5px]"><span className="text-dc-ink3">약세 시 유형:</span> {scen.bear_type}</p>
              )}
            </div>
      )}

      {/* 시나리오 탭이 AI 로딩 중이면 빈 화면 대신 안내 */}
      {loadingScen && !(scen?.scenarios?.length) && (
        <p className="flex items-center gap-2 text-dc-ink2 text-[11.5px] py-3">
          <Loader2 size={13} className="animate-spin" /> 시나리오 불러오는 중...
        </p>
      )}

      {/* ===== 성적표 통합(재편 07-07) — 이 탭의 모든 예측은 여기서 채점된다 ===== */}
      <div className="mt-5 border-t border-dc-hair pt-4">
        <p className={`${KICKER} mb-2`}>성적표 — 이 탭의 모든 예측, 채점됩니다 <span className={`${PLAIN} text-dc-ink3/80`}>· 예보(방향) / 국면 시나리오 / 종목 심층분석</span></p>
        <ForecastCard />
        {isOwner() && <div className="mt-3"><AiVerifyPanel track={trackStats} ai={aiStats} /></div>}
      </div>
      </>)}
    </div>
  );
}
